/**
 * Picking the GPS fix to send. The phone watches for a few seconds and keeps the most
 * accurate reading. No geofence (DECISIONS.md #6): we only warn above 25 m.
 */

export const ACCURACY_WARN_M = 25;
/** stop watching early once a fix is this good (and we watched for at least MIN_WATCH_MS) */
export const ACCURACY_GOOD_M = 8;
export const MIN_WATCH_MS = 4_000;
export const MAX_WATCH_MS = 30_000;

export interface Fix {
  lat: number;
  lng: number;
  /** metres, 68% confidence radius as reported by the OS; null if unknown */
  accuracyM: number | null;
  /** epoch ms when the fix was taken */
  timestamp: number;
}

/** Lower accuracy radius wins; a fix with unknown accuracy only beats nothing. */
export function betterFix(current: Fix | null, next: Fix): Fix {
  if (!current) return next;
  if (next.accuracyM == null) return current;
  if (current.accuracyM == null) return next;
  return next.accuracyM < current.accuracyM ? next : current;
}

export function isAccurate(fix: Fix | null): boolean {
  return !!fix && fix.accuracyM != null && fix.accuracyM <= ACCURACY_WARN_M;
}

/** Enough to stop watching: a good fix after the minimum watch time, or the time is up. */
export function shouldStopWatching(best: Fix | null, startedAt: number, now: number): boolean {
  const elapsed = now - startedAt;
  if (elapsed >= MAX_WATCH_MS) return true;
  return elapsed >= MIN_WATCH_MS && !!best && best.accuracyM != null && best.accuracyM <= ACCURACY_GOOD_M;
}

/**
 * Same bounding box the API enforces (lat 6–37.6, lng 68–97.5). A point outside it would be
 * rejected for good, so the app says so before queueing.
 */
export function insideIndia(lat: number, lng: number): boolean {
  return lat >= 6 && lat <= 37.6 && lng >= 68 && lng <= 97.5;
}

export function roundAccuracy(m: number | null): number | null {
  return m == null ? null : Math.round(m * 10) / 10;
}

export function mapsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}
